import { useCallback } from 'react';

import { type AirActionTrackingLocation, useTrackRemovedTagsFromAssets } from '@air/analytics';
import { Tags } from '@air/api';
import { type Clip } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type TagBase } from '~/components/Tags/utils';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

type RemoveTagsFromClipsParams = {
  tags: TagBase[];
  clipIds: Clip['id'][];
  trackLocation: AirActionTrackingLocation;
};

export const useRemoveTagsFromClips = () => {
  const { trackRemovedTagsFromAssets } = useTrackRemovedTagsFromAssets();
  const { currentWorkspace } = useCurrentWorkspace();

  const removeTagsFromClips = useCallback(
    async ({ tags, clipIds, trackLocation }: RemoveTagsFromClipsParams) => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        throw new Error('No workspace id');
      }
      try {
        await Tags.removeFromClips({ workspaceId, idsToRemove: tags.map(({ id }) => id), clipIds });
        trackRemovedTagsFromAssets({
          assets: clipIds,
          tags: tags.map((tag) => ({ ...tag, isDemo: false })),
          location: trackLocation,
        });
      } catch (error) {
        throw reportErrorToBugsnag({
          error,
          context: 'Failed to remove tags from clips',
          metadata: {
            data: {
              tags,
              clipIds,
            },
          },
        });
      }
    },
    [trackRemovedTagsFromAssets, currentWorkspace?.id],
  );

  return { removeTagsFromClips };
};
